"use client";

import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { ImperativePanelGroupHandle } from "react-resizable-panels";

const SAFE_MIN_PERCENT = 1;
const SAFE_MAX_PERCENT = 99;

export interface UseFixedPixelPanelOptions {
  /** localStorage key used to persist the controlled panel's pixel size. */
  storageKey: string;
  /** Pixel size to use when nothing is persisted yet. */
  defaultPx: number;
  /** Minimum pixel size enforced for the controlled panel. */
  minPx: number;
  /** Layout direction of the parent `ResizablePanelGroup`. */
  direction: "horizontal" | "vertical";
  /** Index of the controlled panel inside the group (0 = first, 1 = second). */
  controlledIndex: 0 | 1;
  /** Minimum percent reserved for the sibling panel; matches its own `minSize`. */
  otherMinPercent: number;
  /**
   * When true, force the controlled panel's layout to 0% (sibling takes 100%)
   * and report a 0 min percent. The stored pixel size is preserved so that
   * toggling back to `false` restores the previous size.
   */
  isCollapsed?: boolean;
}

export interface UseFixedPixelPanelResult {
  containerRef: RefObject<HTMLDivElement>;
  groupRef: RefObject<ImperativePanelGroupHandle>;
  /** Percent to use as the controlled panel's `defaultSize` on first render. */
  initialPercent: number;
  /** Dynamic `minSize` (percent) for the controlled panel based on container size. */
  controlledMinPercent: number;
  /** Wire to `ResizablePanelGroup`'s `onLayout`. */
  handleLayout: (layout: number[]) => void;
  /** Wire to `ResizableHandle`'s `onDragging`. */
  handleDragging: (dragging: boolean) => void;
}

function readPx(key: string, fallback: number): number {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function writePx(key: string, value: number) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, String(Math.round(value)));
  } catch {
    // Quota / private-mode failures are non-fatal; ignore.
  }
}

function clampPercent(value: number): number {
  return Math.min(Math.max(value, SAFE_MIN_PERCENT), SAFE_MAX_PERCENT);
}

/**
 * Keeps a `react-resizable-panels` panel sized in pixels rather than percent.
 *
 * The library always layouts in percentages, so this hook observes the parent
 * container size and reapplies a percent layout that matches the user's chosen
 * pixel size. User drags update the stored pixel value; window resizes do not.
 */
export function useFixedPixelPanel({
  storageKey,
  defaultPx,
  minPx,
  direction,
  controlledIndex,
  otherMinPercent,
  isCollapsed = false,
}: UseFixedPixelPanelOptions): UseFixedPixelPanelResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<ImperativePanelGroupHandle>(null);
  const desiredPxRef = useRef<number>(defaultPx);
  const containerSizeRef = useRef<number>(0);
  const draggingRef = useRef<boolean>(false);

  const [initialPercent] = useState<number>(() => {
    const px = readPx(storageKey, defaultPx);
    desiredPxRef.current = px;
    if (typeof window === "undefined") return 25;
    const viewportTotal =
      direction === "horizontal" ? window.innerWidth : window.innerHeight;
    if (viewportTotal <= 0) return 25;
    return clampPercent((px / viewportTotal) * 100);
  });

  const [controlledMinPercent, setControlledMinPercent] =
    useState<number>(SAFE_MIN_PERCENT);

  const applyLayout = useCallback(() => {
    const group = groupRef.current;
    const totalPx = containerSizeRef.current;
    if (!group || totalPx <= 0) return;

    let layout: number[];

    if (isCollapsed) {
      layout = controlledIndex === 0 ? [0, 100] : [100, 0];
    } else {
      const maxControlledPx = Math.max(
        minPx,
        totalPx * (1 - otherMinPercent / 100),
      );
      const targetPx = Math.min(
        Math.max(desiredPxRef.current, minPx),
        maxControlledPx,
      );
      const targetPercent = clampPercent((targetPx / totalPx) * 100);
      const otherPercent = 100 - targetPercent;

      layout =
        controlledIndex === 0
          ? [targetPercent, otherPercent]
          : [otherPercent, targetPercent];
    }

    const current = group.getLayout();
    if (
      current.length === 2 &&
      Math.abs(current[controlledIndex] - layout[controlledIndex]) < 0.05
    ) {
      return;
    }

    group.setLayout(layout);
  }, [controlledIndex, isCollapsed, minPx, otherMinPercent]);

  const updateMinPercent = useCallback(
    (totalPx: number) => {
      if (totalPx <= 0) return;
      const next = isCollapsed
        ? 0
        : (() => {
            const maxAllowed = Math.max(
              SAFE_MIN_PERCENT,
              100 - otherMinPercent - 1,
            );
            const rawPercent = (minPx / totalPx) * 100;
            return Math.min(
              Math.max(rawPercent, SAFE_MIN_PERCENT),
              maxAllowed,
            );
          })();
      setControlledMinPercent((prev) =>
        Math.abs(prev - next) < 0.05 ? prev : next,
      );
    },
    [isCollapsed, minPx, otherMinPercent],
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (typeof ResizeObserver === "undefined") return;

    const measure = () => {
      const rect = container.getBoundingClientRect();
      const size = direction === "horizontal" ? rect.width : rect.height;
      if (size <= 0) return;
      containerSizeRef.current = size;
      updateMinPercent(size);
      applyLayout();
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [applyLayout, direction, updateMinPercent]);

  const handleLayout = useCallback(
    (layout: number[]) => {
      if (!draggingRef.current) return;
      if (isCollapsed) return;
      const totalPx = containerSizeRef.current;
      if (totalPx <= 0 || layout.length < 2) return;
      const percent = layout[controlledIndex];
      const px = (percent / 100) * totalPx;
      desiredPxRef.current = px;
      writePx(storageKey, px);
    },
    [controlledIndex, isCollapsed, storageKey],
  );

  const handleDragging = useCallback((dragging: boolean) => {
    draggingRef.current = dragging;
  }, []);

  return {
    containerRef,
    groupRef,
    initialPercent,
    controlledMinPercent,
    handleLayout,
    handleDragging,
  };
}
